import { useCallback, useEffect, useState } from 'react';
import { useFeatureServices } from '../hooks/use-feature-services';
import { StoreModel, Action } from './types';
import { getInitialState, rootReducer } from './slices';

let globalState: StoreModel;
let listeners: ((state: StoreModel) => void)[] = [];

export const cleanStore = (memoryStore: any) => {
  memoryStore.setState(getInitialState());
  globalState = getInitialState();
  listeners = [];
};

export const useStore = (
  shouldListen = true
): [StoreModel, (action: Action) => void] => {
  // This is the way to synchronize SSR and CSR, the store state is created
  // in base of feature-app-memory-store to keep the information in both sides
  const { 'feature-app-memory-store': memoryStore } = useFeatureServices();

  const memoryState = memoryStore.getState();

  // Priority: memory store state (from SSR) > globalState > initial state
  // This ensures SSR data is never overwritten
  if (memoryState) {
    // Memory store has state (from SSR) - use it and sync globalState
    if (!globalState || globalState !== memoryState) {
      globalState = memoryState;
    }
  } else {
    // No memory store state - use globalState or initial state
    if (!globalState) {
      globalState = getInitialState();
    }
  }

  const [state, setState] = useState<StoreModel>(globalState);

  const dispatch = useCallback((action: Action) => {
    const newState = rootReducer(globalState, action);
    globalState = newState;

    // Change the state of the memory store to transfer it from one server-side
    // render pass to another.
    memoryStore.setState(globalState);

    listeners.forEach((l) => l(globalState));

    return action;
  }, []);

  useEffect(() => {
    if (shouldListen) {
      listeners.push(setState);
    }
    return () => {
      if (shouldListen) {
        listeners = listeners.filter((l) => l !== setState);
      }
    };
  }, [shouldListen]);

  return [state, dispatch];
};
